import { useState, type FormEvent, type InputHTMLAttributes } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Heading,
  Input,
  SelectField,
  tabularNumsClass,
  Text,
} from "@lendcord/concord";
import { KycDetailsSkeleton } from "./KycDetailsSkeleton";
import { ProfileField } from "@/features/profile/components/ProfileField";
import {
  useCreateNomineeMutation,
  useDeleteNomineeMutation,
  useGetProfileQuery,
  useRevealPiiMutation,
  useUpdateNomineeMutation,
  useUpdateProfileIdentityMutation,
} from "@/app/profile/api/profile.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { useToast } from "@/app/toast";
import { getRoleFromToken, getUserIdFromToken } from "@/app/tokenStorage";
import { formatWireDate, isoToWireDate, wireDateToIso } from "@/app/dateFormat";
import { AADHAAR_REGEX, isAtLeast18, PAN_REGEX } from "@/app/profile/validators";
import { NAME_REGEX, PHONE_REGEX, toIndianE164 } from "@/auth/validators";
import type { NomineeRelationship, Profile } from "@/app/profile/profile.types";

// The nominee's phone is stored the same way the borrower's own phone is
// (E.164, +91-only for now) — strip the prefix back off for editing since
// the field only ever collects the 10-digit national number.
function stripIndianE164(value: string): string {
  return value.startsWith("+91") ? value.slice(3) : value;
}

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const GENDER_LABEL_IDS: Record<string, string> = {
  MALE: "lender.profile.genderMale",
  FEMALE: "lender.profile.genderFemale",
  OTHER: "lender.profile.genderOther",
};

const RELATIONSHIP_LABEL_IDS: Record<NomineeRelationship, string> = {
  SPOUSE: "lender.profile.relationshipSpouse",
  PARENT: "lender.profile.relationshipParent",
  CHILD: "lender.profile.relationshipChild",
  SIBLING: "lender.profile.relationshipSibling",
  OTHER: "lender.profile.relationshipOther",
};

type PhoneInputProps = { value: string; onChange: (value: string) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
>;

// FormField clones its single child and injects id/aria/state props onto it —
// this wrapper forwards those onto the real <input> so the +91 prefix chip
// doesn't break the label[for] <-> input[id] association (same pattern as
// auth/components/CredentialsForm.tsx's PhoneInput).
function PhoneInput({ value, onChange, ...controlProps }: PhoneInputProps) {
  return (
    <div className="flex">
      <span
        className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted"
        aria-hidden="true"
      >
        +91
      </span>
      <Input
        {...controlProps}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 10))}
        inputMode="numeric"
        autoComplete="tel-national"
        className="flex-1"
      />
    </div>
  );
}

interface KycDetailsProps {
  profile: Profile | null;
}

function EyeIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-10-7-10-7a18.37 18.37 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a18.5 18.5 0 0 1-2.06 3.06" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function KycDetails({ profile }: KycDetailsProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const { refetch } = useGetProfileQuery();
  const [revealPii] = useRevealPiiMutation();
  const [updateProfileIdentity] = useUpdateProfileIdentityMutation();
  const [createNominee] = useCreateNomineeMutation();
  const [updateNominee] = useUpdateNomineeMutation();
  const [deleteNominee, { isLoading: isDeletingNominee }] = useDeleteNomineeMutation();
  const [isRemoveNomineeConfirmOpen, setIsRemoveNomineeConfirmOpen] = useState(false);
  const [removeNomineeError, setRemoveNomineeError] = useState<string | undefined>();

  const [isEditing, setIsEditing] = useState(false);
  const [revealedData, setRevealedData] = useState<{ pan?: string; aadhaar?: string }>({});
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());
  const [revealing, setRevealing] = useState<Set<string>>(new Set());
  const [pan, setPan] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [dob, setDob] = useState("");
  const [fathersName, setFathersName] = useState("");
  const [gender, setGender] = useState("");
  const [nomineeName, setNomineeName] = useState("");
  const [nomineeContact, setNomineeContact] = useState("");
  const [nomineeRelationship, setNomineeRelationship] = useState<NomineeRelationship | "">("");
  const [errors, setErrors] = useState<{
    pan?: string;
    aadhaar?: string;
    dob?: string;
    fathersName?: string;
    nomineeName?: string;
    nomineeContact?: string;
    nomineeRelationship?: string;
  }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Name + relationship become required together the moment any nominee
  // field is touched — the backend requires both on create/update.
  const hasNomineeInput = Boolean(nomineeName.trim() || nomineeRelationship || nomineeContact);

  if (!profile) {
    return <KycDetailsSkeleton />;
  }

  function handleStartEdit() {
    setPan("");
    setAadhaar("");
    setDob(profile!.dob ? wireDateToIso(profile!.dob) : "");
    setFathersName(profile!.fathersName ?? "");
    setGender(profile!.gender ?? "");
    setNomineeName(profile!.nominee?.name ?? "");
    setNomineeContact(profile!.nominee?.phone ? stripIndianE164(profile!.nominee.phone) : "");
    setNomineeRelationship(profile!.nominee?.relationship ?? "");
    setErrors({});
    setFormError(undefined);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  async function handleRemoveNominee() {
    setRemoveNomineeError(undefined);
    try {
      await deleteNominee().unwrap();
      setIsRemoveNomineeConfirmOpen(false);
    } catch (error) {
      setRemoveNomineeError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.removeNomineeError" }),
      );
    }
  }

  async function handleReveal(field: "pan" | "aadhaar") {
    if (revealedFields.has(field)) {
      setRevealedFields((prev) => { const next = new Set(prev); next.delete(field); return next; });
      return;
    }
    if (revealing.has(field)) return;

    const userId = getUserIdFromToken();
    if (!userId) {
      showToast({
        status: "error",
        title: intl.formatMessage({ id: "lender.profile.revealErrorTitle" }),
        description: intl.formatMessage({ id: "lender.profile.revealError" }),
      });
      return;
    }

    setRevealing((prev) => new Set(prev).add(field));
    try {
      const { value } = await revealPii({ dataType: field === "pan" ? "PAN" : "AADHAAR", userId }).unwrap();
      setRevealedData((prev) => ({ ...prev, [field]: value }));
      setRevealedFields((prev) => new Set(prev).add(field));
    } catch (error) {
      showToast({
        status: "error",
        title: intl.formatMessage({ id: "lender.profile.revealErrorTitle" }),
        description: isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.revealError" }),
      });
    } finally {
      setRevealing((prev) => { const next = new Set(prev); next.delete(field); return next; });
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (pan && !PAN_REGEX.test(pan)) {
      nextErrors.pan = intl.formatMessage({ id: "lender.profile.panFormatError" });
    }
    if (aadhaar && !AADHAAR_REGEX.test(aadhaar)) {
      nextErrors.aadhaar = intl.formatMessage({ id: "lender.profile.aadhaarFormatError" });
    }
    if (dob && new Date(dob) >= new Date()) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobFutureError" });
    } else if (dob && !isAtLeast18(dob)) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobMinAgeError" });
    }
    if (fathersName && !NAME_REGEX.test(fathersName)) {
      nextErrors.fathersName = intl.formatMessage({ id: "lender.profile.fathersNameFormatError" });
    }

    if (hasNomineeInput) {
      if (!nomineeName.trim()) {
        nextErrors.nomineeName = intl.formatMessage({ id: "lender.profile.nomineeNameRequiredError" });
      } else if (!NAME_REGEX.test(nomineeName)) {
        nextErrors.nomineeName = intl.formatMessage({ id: "lender.profile.fathersNameFormatError" });
      }
      if (!nomineeRelationship) {
        nextErrors.nomineeRelationship = intl.formatMessage({
          id: "lender.profile.nomineeRelationshipRequiredError",
        });
      }
      if (nomineeContact && !PHONE_REGEX.test(nomineeContact)) {
        nextErrors.nomineeContact = intl.formatMessage({ id: "lender.profile.nomineeContactFormatError" });
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await updateProfileIdentity({
        pan: pan || undefined,
        aadhaar: aadhaar || undefined,
        dob: dob ? isoToWireDate(dob) : undefined,
        fathersName: fathersName.trim() || undefined,
        gender: gender || undefined,
      }).unwrap();

      if (hasNomineeInput) {
        const payload = {
          name: nomineeName.trim(),
          relationship: nomineeRelationship as NomineeRelationship,
          phone: nomineeContact ? toIndianE164(nomineeContact) : undefined,
        };
        if (profile!.nominee) {
          await updateNominee(payload).unwrap();
        } else {
          await createNominee(payload).unwrap();
        }
      }

      await refetch();
      setIsEditing(false);
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-040") {
        setErrors({ pan: intl.formatMessage({ id: "lender.profile.panTakenError" }) });
      } else if (isQueryApiError(error) && error.code === "LEND-041") {
        setErrors({ aadhaar: intl.formatMessage({ id: "lender.profile.aadhaarTakenError" }) });
      } else {
        setFormError(intl.formatMessage({ id: "lender.profile.identityUpdateError" }));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <Heading level={3}>
          <FormattedMessage id="lender.profile.kycDetailsHeading" />
        </Heading>

        {formError && <AlertBanner status="error">{formError}</AlertBanner>}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.panLabel" })}
            error={errors.pan}
            hint={errors.pan ? undefined : intl.formatMessage({ id: "lender.profile.panEditHint" })}
          >
            <Input
              value={pan}
              onChange={(event) => setPan(event.target.value.toUpperCase())}
              placeholder={intl.formatMessage({ id: "lender.profile.panPlaceholder" })}
              maxLength={10}
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.aadhaarLabel" })}
            error={errors.aadhaar}
            hint={errors.aadhaar ? undefined : intl.formatMessage({ id: "lender.profile.aadhaarEditHint" })}
          >
            <Input
              value={aadhaar}
              onChange={(event) => setAadhaar(event.target.value.replace(/\D/g, ""))}
              placeholder={intl.formatMessage({ id: "lender.profile.aadhaarPlaceholder" })}
              maxLength={12}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.fathersNameLabel" })}
            error={errors.fathersName}
          >
            <Input value={fathersName} onChange={(event) => setFathersName(event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.profile.dateOfBirthLabel" })} error={errors.dob}>
            <Input type="date" value={dob} onChange={(event) => setDob(event.target.value)} />
          </FormField>
        </div>

        <SelectField
          label={intl.formatMessage({ id: "lender.profile.genderLabel" })}
          placeholder={intl.formatMessage({ id: "lender.profile.genderPlaceholder" })}
          value={gender}
          onValueChange={setGender}
          options={[
            { value: "MALE", label: intl.formatMessage({ id: "lender.profile.genderMale" }) },
            { value: "FEMALE", label: intl.formatMessage({ id: "lender.profile.genderFemale" }) },
            { value: "OTHER", label: intl.formatMessage({ id: "lender.profile.genderOther" }) },
          ]}
        />

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.nomineeDetailsHeading" />
        </Heading>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.nomineeNameLabel" })}
            error={errors.nomineeName}
            required={hasNomineeInput}
          >
            <Input value={nomineeName} onChange={(event) => setNomineeName(event.target.value)} />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.nomineeContactNumberLabel" })}
            error={errors.nomineeContact}
          >
            <PhoneInput value={nomineeContact} onChange={setNomineeContact} />
          </FormField>
        </div>

        <SelectField
          label={intl.formatMessage({ id: "lender.profile.nomineeRelationshipLabel" })}
          placeholder={intl.formatMessage({ id: "lender.profile.relationshipPlaceholder" })}
          value={nomineeRelationship}
          onValueChange={(value) => setNomineeRelationship(value as NomineeRelationship)}
          error={errors.nomineeRelationship}
          required={hasNomineeInput}
          options={[
            { value: "SPOUSE", label: intl.formatMessage({ id: "lender.profile.relationshipSpouse" }) },
            { value: "PARENT", label: intl.formatMessage({ id: "lender.profile.relationshipParent" }) },
            { value: "CHILD", label: intl.formatMessage({ id: "lender.profile.relationshipChild" }) },
            { value: "SIBLING", label: intl.formatMessage({ id: "lender.profile.relationshipSibling" }) },
            { value: "OTHER", label: intl.formatMessage({ id: "lender.profile.relationshipOther" }) },
          ]}
        />

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="lender.profile.savingButton" />
            ) : (
              <FormattedMessage id="lender.profile.saveButton" />
            )}
          </Button>
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            <FormattedMessage id="lender.profile.cancelButton" />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <Heading level={3}>
          <FormattedMessage id="lender.profile.kycDetailsHeading" />
        </Heading>
        <Button type="button" variant="ghost" density="compact" onClick={handleStartEdit}>
          <FormattedMessage id="lender.profile.editButton" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <Text as="p" size="xs" variant="muted">
            <FormattedMessage id="lender.profile.panLabel" />
          </Text>
          <div className="mt-0.5 flex items-center gap-2">
            <Text as="p" size="sm" className="font-medium">
              {revealedFields.has("pan") ? (revealedData.pan ?? "–") : (profile.pan ?? "–")}
            </Text>
            <button
              type="button"
              onClick={() => handleReveal("pan")}
              disabled={revealing.has("pan")}
              className="shrink-0 text-text-muted hover:text-text"
              aria-label={intl.formatMessage({ id: revealedFields.has("pan") ? "lender.profile.hidePanLabel" : "lender.profile.revealPanLabel" })}
            >
              {revealedFields.has("pan") ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Text as="p" size="xs" variant="muted">
            <FormattedMessage id="lender.profile.aadhaarLabel" />
          </Text>
          <div className="mt-0.5 flex items-center gap-2">
            <Text as="p" size="sm" className="font-medium">
              {revealedFields.has("aadhaar") ? (revealedData.aadhaar ?? "–") : (profile.aadhaar ?? "–")}
            </Text>
            <button
              type="button"
              onClick={() => handleReveal("aadhaar")}
              disabled={revealing.has("aadhaar")}
              className="shrink-0 text-text-muted hover:text-text"
              aria-label={intl.formatMessage({ id: revealedFields.has("aadhaar") ? "lender.profile.hideAadhaarLabel" : "lender.profile.revealAadhaarLabel" })}
            >
              {revealedFields.has("aadhaar") ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <ProfileField labelId="lender.profile.fathersNameLabel" value={profile.fathersName ?? "–"} />
        <ProfileField
          labelId="lender.profile.dateOfBirthLabel"
          value={profile.dob ? formatWireDate(profile.dob) : "–"}
        />
      </div>

      <ProfileField
        labelId="lender.profile.genderLabel"
        value={
          profile.gender
            ? intl.formatMessage({ id: GENDER_LABEL_IDS[profile.gender] ?? "lender.profile.genderOther" })
            : "–"
        }
      />

      {/* loanAmount is structurally only ever set for a BORROWER (see
          Profile.loanAmount's doc comment) — LENDER/STAFF/AGENT/ENTERPRISE/
          ADMIN never have a role_applications loan amount, so this stays
          hidden for them rather than showing an always-empty field. */}
      {getRoleFromToken() === "BORROWER" && (
        <ProfileField
          labelId="lender.profile.loanAmountLabel"
          value={profile.loanAmount != null ? currencyFormatter.format(profile.loanAmount) : "–"}
          valueClassName={tabularNumsClass}
        />
      )}

      <div className="flex items-center justify-between gap-3">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.nomineeDetailsHeading" />
        </Heading>
        {profile.nominee && (
          <Button
            type="button"
            variant="ghost"
            density="compact"
            className="text-error-fg hover:bg-error-bg"
            onClick={() => setIsRemoveNomineeConfirmOpen(true)}
          >
            <FormattedMessage id="lender.profile.removeNomineeButton" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <ProfileField labelId="lender.profile.nomineeNameLabel" value={profile.nominee?.name ?? "–"} />
        <ProfileField labelId="lender.profile.nomineeContactNumberLabel" value={profile.nominee?.phone ?? "–"} />
      </div>

      <ProfileField
        labelId="lender.profile.nomineeRelationshipLabel"
        value={
          profile.nominee?.relationship
            ? intl.formatMessage({ id: RELATIONSHIP_LABEL_IDS[profile.nominee.relationship] })
            : "–"
        }
      />

      <Dialog open={isRemoveNomineeConfirmOpen} onOpenChange={setIsRemoveNomineeConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="lender.profile.removeNomineeConfirmHeading" />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage id="lender.profile.removeNomineeConfirmDescription" />
            </DialogDescription>
          </DialogHeader>

          {removeNomineeError && <AlertBanner status="error">{removeNomineeError}</AlertBanner>}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsRemoveNomineeConfirmOpen(false)}
              disabled={isDeletingNominee}
            >
              <FormattedMessage id="lender.profile.deleteAccountCancelButton" />
            </Button>
            <Button type="button" variant="destructive" onClick={handleRemoveNominee} disabled={isDeletingNominee}>
              {isDeletingNominee ? (
                <FormattedMessage id="lender.profile.removingNomineeButton" />
              ) : (
                <FormattedMessage id="lender.profile.removeNomineeButton" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
